import type { Session } from "@supabase/supabase-js";

export const ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY = "applyt:anonymous-session-transfer:v1";

export interface PendingAnonymousSessionTransfer {
  sourceUserId: string;
  anonymousAccessToken: string;
  capturedAt: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const getSessionStorage = (): StorageLike | null => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
};

const isAnonymousSession = (session: Session | null | undefined): session is Session & {
  user: Session["user"] & { is_anonymous: true };
} => (session?.user as (Session["user"] & { is_anonymous?: boolean }) | undefined)?.is_anonymous === true;

export function createPendingAnonymousSessionTransfer(
  session: Session | null | undefined,
): PendingAnonymousSessionTransfer | null {
  if (!isAnonymousSession(session) || !session.access_token || !session.user.id) return null;
  return {
    sourceUserId: session.user.id,
    anonymousAccessToken: session.access_token,
    capturedAt: new Date().toISOString(),
  };
}

export function createPendingAnonymousSessionTransferForAuthChange(
  previousSession: Session | null | undefined,
  nextSession: Session | null | undefined,
): PendingAnonymousSessionTransfer | null {
  if (
    !isAnonymousSession(previousSession)
    || !nextSession?.user
    || (nextSession.user as Session["user"] & { is_anonymous?: boolean }).is_anonymous === true
    || previousSession.user.id === nextSession.user.id
  ) {
    return null;
  }

  return createPendingAnonymousSessionTransfer(previousSession);
}

export function readPendingAnonymousSessionTransfer(
  storage: StorageLike | null = getSessionStorage(),
): PendingAnonymousSessionTransfer | null {
  if (!storage) return null;
  const raw = storage.getItem(ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<PendingAnonymousSessionTransfer>;
    if (
      typeof value.sourceUserId !== "string" || !value.sourceUserId
      || typeof value.anonymousAccessToken !== "string" || !value.anonymousAccessToken
      || typeof value.capturedAt !== "string" || !value.capturedAt
    ) {
      storage.removeItem(ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY);
      return null;
    }
    return value as PendingAnonymousSessionTransfer;
  } catch {
    storage.removeItem(ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY);
    return null;
  }
}

export function persistPendingAnonymousSessionTransfer(
  pending: PendingAnonymousSessionTransfer,
  storage: StorageLike | null = getSessionStorage(),
): void {
  storage?.setItem(ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY, JSON.stringify(pending));
}

export function clearPendingAnonymousSessionTransfer(
  storage: StorageLike | null = getSessionStorage(),
): void {
  storage?.removeItem(ANONYMOUS_SESSION_TRANSFER_STORAGE_KEY);
}
